import Link from 'next/link'
import React from 'react'
import SeoCategorySearch from '@/components/seo/SeoCategorySearch'

/**
 * Components available to every SEO page's MDX.
 *
 * Element overrides keep the copy free of markup: the source is plain markdown, and
 * anything structural is applied here so all ~70 pages stay consistent.
 */
type MdxLinkProps = Omit<React.ComponentProps<'a'>, 'ref'>

const MdxLink = ({ href, children, ...rest }: MdxLinkProps) => {
  if (!href) return <a {...rest}>{children}</a>

  // Cross-links are written as the public url of the locale the copy is in, so they need
  // no localizing here. Next adds the locale prefix to a Link on a prefixed page.
  const isInternal = href.startsWith('/')
  if (!isInternal) {
    return (
      <a href={href} rel="noopener noreferrer" target="_blank" {...rest}>
        {children}
      </a>
    )
  }

  return (
    <Link href={href} {...rest}>
      {children}
    </Link>
  )
}

/**
 * The page heading, with the category search directly under it. It goes here rather than
 * in the page wrapper because the heading lives in the MDX body: every page opens with
 * exactly one h1, so this is the seam between the heading and the copy.
 *
 * The category roots render their copy through SeoContent alone, outside the provider
 * SeoPage sets up, so the category reads as null there and they keep their own search.
 */
const MdxHeading = ({ children, ...rest }: React.ComponentProps<'h1'>) => (
  <>
    <h1 {...rest}>{children}</h1>
    <SeoCategorySearch />
  </>
)

const mdxComponents = {
  a: MdxLink,
  h1: MdxHeading,
}

export default mdxComponents
